import React, { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import InboxRoundedIcon from '@mui/icons-material/InboxRounded';
import SearchOffRoundedIcon from '@mui/icons-material/SearchOffRounded';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';

export interface NoDataStateProps {
  title: string;
  message: string;
  actionLabel?: string;
  onAction?: () => void;
  illustration?: 'empty' | 'search' | 'assessment' | 'error' | 'loading';
  className?: string;
}

const renderIllustration = (illustration: string): ReactNode => {
  const iconSx = { fontSize: 56, color: 'text.disabled' };

  switch (illustration) {
    case 'search':
      return <SearchOffRoundedIcon sx={iconSx} />;
    case 'assessment':
      return <AssignmentOutlinedIcon sx={iconSx} />;
    case 'error':
      return <ErrorOutlineRoundedIcon sx={{ ...iconSx, color: 'error.light' }} />;
    case 'loading':
      return <CircularProgress size={48} thickness={4} />;
    case 'empty':
    default:
      return <InboxRoundedIcon sx={iconSx} />;
  }
};

export const NoDataState = ({
  title,
  message,
  actionLabel,
  onAction,
  illustration = 'empty',
  className,
}: NoDataStateProps) => {
  return (
    <Box
      className={className}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 340,
        bgcolor: 'background.paper',
        borderRadius: '16px',
        px: 3,
        py: 6,
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          mb: 2.5,
          p: 2,
          borderRadius: '50%',
          bgcolor: 'grey.50',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'transform 0.3s ease',
          '&:hover': {
            transform: 'scale(1.08)',
          },
        }}
      >
        {renderIllustration(illustration)}
      </Box>

      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: 'text.primary',
          mb: 1,
          maxWidth: 480,
        }}
      >
        {title}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mb: 3,
          maxWidth: 440,
          lineHeight: 1.5,
        }}
      >
        {message}
      </Typography>

      {actionLabel && onAction && (
        <Button
          variant="contained"
          color="primary"
          onClick={onAction}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            py: 1,
            boxShadow: 'none',
          }}
        >
          {actionLabel}
        </Button>
      )}
    </Box>
  );
};

export default NoDataState;
